import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { CalendarDays, Scale, FileText, ShieldAlert } from "lucide-react";
import PageMeta from "./PageMeta";

export const fixtureCards = [
  {
    date: "Sun 1st – Sat 28th February",
    isoDate: "2026-02-01",
    type: "Rover Competition",
    venue: "Rover – best % of 2 fish of any species, 1 of each species",
    time: "Start 8:00am Sun 1st Feb · Weigh-in by 9:30pm Sat 28th Feb",
    venueType: "shore",
    rules: "/assets/month_long_shore_comps.pdf",
    monthLong: true,
  },
  {
    date: "Sun 29th March",
    isoDate: "2026-03-29",
    type: "Easter Competition",
    venue: "Shore – any venue",
    time: "Start 8:00am · Last Fish Sun 12th Apr 9:30pm · Best total % from 4 different species",
    venueType: "shore",
    rules: "/assets/competition_rules/RSAA_Easter_comp_rules.pdf",
  },
  {
    date: "Sun 17th May",
    isoDate: "2026-05-17",
    type: "Colin Rule (1)",
    venue: "Weigh-in: Sea View, Treskerby, Redruth, TR15 1RG",
    time: "Start 8:00am · Weigh-in 9:30pm · High Tide 6:30pm",
    venueType: "shore",
    rules: "/assets/competition_rules/RSAA_Colin_Rule_Final.pdf",
  },
  {
    date: "Mon 1st – Tue 30th June",
    isoDate: "2026-06-01",
    type: "Small Boats / Dinghy Competition",
    venue: "Rover – privately owned boats/watercraft under 8m",
    time: "Start 8am Mon 1st Jun · Weigh-in by 9:30pm 30th Jun · Best % 2 species",
    venueType: "boat",
  },
  {
    date: "Sun 7th June",
    isoDate: "2026-06-07",
    type: "Half Day Club Boat Trip",
    venue: "South Quay · Che Sara Sara (Dave Trebilcock)",
    time: "Start 9:00am · Finish 1:00pm",
    venueType: "boat",
  },
  {
    date: "Sun 14th June",
    isoDate: "2026-06-14",
    type: "Colin Rule (2)",
    venue: "Weigh-in: Sea View, Treskerby, Redruth, TR15 1RG",
    time: "Start 8:00am · Weigh-in 9:30pm · High Tide 7:50pm",
    venueType: "shore",
    rules: "/assets/competition_rules/RSAA_Colin_Rule_Final.pdf",
  },
  {
    date: "Sat 27th June",
    isoDate: "2026-06-27",
    type: "Half Day Club Boat Trip",
    venue: "North Quay · Che Sara Sara (Dave Trebilcock)",
    time: "Start 9:00am · Finish 1:00pm",
    venueType: "boat",
  },
  {
    date: "Sat 1st August",
    isoDate: "2026-08-01",
    type: "Bream Competition",
    venue: "Total cumulative percentage for up to two bream species (maximum one fish from each species). Where both one-species and two-species combinations are possible, the two-species combination takes precedence.",
    time: "Normal weigh-in rules apply",
    venueType: "shore",
    rules: "/assets/month_long_shore_comps.pdf",
    monthLong: true,
  },
  {
    date: "Sun 23rd August",
    isoDate: "2026-08-23",
    type: "Full Day Club Boat Trip",
    venue: "Bite Adventures (Robin Chapman)",
    time: "Full day trip – details to follow",
    venueType: "boat",
  },
  {
    date: "Sun 30th August",
    isoDate: "2026-08-30",
    type: "Colin Rule (3)",
    venue: "Weigh-in: Sea View, Treskerby, Redruth, TR15 1RG",
    time: "Start 8:00am · Weigh-in 9:30pm · High Tide 7:35pm",
    venueType: "shore",
    rules: "/assets/competition_rules/RSAA_Colin_Rule_Final.pdf",
  },
  {
    date: "Sun 27th September",
    isoDate: "2026-09-27",
    type: "Float and Spin Competition",
    venue: "Weigh-in: Sea View, Treskerby, Redruth, TR15 1RG",
    time: "Start 8:00am · Weigh-in 9:30pm · High Tide 6:30pm",
    venueType: "shore",
    rules: "/assets/competition_rules/RSAA_Float_Spin_Competition_2026.pdf",
  },
  {
    date: "Thu 1st October",
    isoDate: "2026-10-01",
    type: "Ray and Bass Competition",
    venue: "TBC – see separate sheet",
    time: "See separate sheet for full details",
    venueType: "shore",
    rules: "/assets/month_long_shore_comps.pdf",
    monthLong: true,
  },
  {
    date: "Sat 17th October",
    isoDate: "2026-10-17",
    type: "Beach Competition",
    venue: "TBC – decided 2 days before",
    time: "Meet 6:30pm · Fish 7:00pm · Weigh-in midnight · High Tide 10:00pm",
    venueType: "shore",
    rules: "/assets/competition_rules/RSAA_Night_Beach_Competition_2026.pdf",
  },
  {
    date: "Sun 15th November",
    isoDate: "2026-11-15",
    type: "Conger Competition",
    venue: "TBC – decided 2 days before",
    time: "Start 4:30pm · Finish 9:00pm",
    venueType: "shore",
    rules: "/assets/competition_rules/RSAA_Conger_Competition_2026.pdf",
  },
  {
    date: "Sun 13th December",
    isoDate: "2026-12-13",
    type: "Colin Rule (4)",
    venue: "Weigh-in: Sea View, Treskerby, Redruth, TR15 1RG",
    time: "Start 8:00am · Weigh-in 9:30pm · High Tide 7:45pm",
    venueType: "shore",
    rules: "/assets/competition_rules/RSAA_Colin_Rule_Final.pdf",
  },
];

const riskAssessments = {
  shore: { label: "Shore Fishing Risk Assessment", file: "/assets/Shore_Fishing_Risk_Assessment.pdf" },
  boat:  { label: "Boat Risk Assessment",           file: "/assets/Boat_Risk_Assessments.pdf" },
};

function RiskAssessmentTabs() {
  const [active, setActive] = useState("shore");
  const current = riskAssessments[active];
  return (
    <div className="mt-6 inline-flex flex-col rounded-2xl border border-amber-400/40 bg-amber-400/10 overflow-hidden">
      <div className="flex">
        {Object.entries(riskAssessments).map(([key, ra]) => (
          <button
            key={key}
            onClick={() => setActive(key)}
            className={`px-5 py-2.5 text-xs font-semibold transition ${
              active === key ? "bg-amber-400/20 text-amber-200" : "text-amber-400/70 hover:text-amber-300"
            }`}
          >
            {key === "shore" ? "Shore Competitions" : "Boat Competitions"}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2.5 border-t border-amber-400/20 px-5 py-3">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 shrink-0 text-amber-300">
          <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
        </svg>
        <span className="text-sm text-amber-200">
          Members competing in {active} competitions must read the{" "}
          <a href={current.file} target="_blank" rel="noopener noreferrer"
            className="font-semibold text-amber-200 underline underline-offset-2 hover:text-white transition">
            {current.label}
          </a>{" "}
          before competing
        </span>
      </div>
    </div>
  );
}

function NextCompetitionBanner() {
  const [countdown, setCountdown] = useState(null);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const next = fixtureCards.find((f) => f.isoDate && new Date(f.isoDate) >= today);

  useEffect(() => {
    if (!next) return;
    const target = new Date(next.isoDate);
    target.setHours(8, 0, 0, 0);

    const update = () => {
      const diff = target - new Date();
      if (diff <= 0) { setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 }); return; }
      setCountdown({
        days:    Math.floor(diff / 86400000),
        hours:   Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      });
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [next]);

  if (!next || !countdown) return null;

  const isToday = countdown.days === 0 && countdown.hours === 0;
  const isSoon  = countdown.days <= 7;

  const Unit = ({ value, label }) => (
    <div className="flex flex-col items-center">
      <span className={`font-extrabold tabular-nums ${isSoon ? "text-4xl sm:text-5xl text-amber-300" : "text-3xl sm:text-4xl text-cyan-300"}`}>
        {String(value).padStart(2, "0")}
      </span>
      <span className={`text-[10px] uppercase tracking-widest mt-0.5 ${isSoon ? "text-amber-200/70" : "text-cyan-300/60"}`}>{label}</span>
    </div>
  );

  return (
    <div
      className={`relative overflow-hidden border-b ${
        isSoon
          ? "border-amber-400/50 bg-gradient-to-r from-amber-500/25 via-amber-400/10 to-transparent shadow-[0_0_50px_-10px_rgba(251,191,36,0.55)]"
          : "border-cyan-400/40 bg-gradient-to-r from-cyan-500/20 via-cyan-400/5 to-transparent shadow-[0_0_35px_-14px_rgba(34,211,238,0.45)]"
      }`}
    >
      <div className={`absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r ${isSoon ? "from-transparent via-amber-400 to-transparent" : "from-transparent via-cyan-400 to-transparent"}`} />
      {isSoon && <div className="pointer-events-none absolute inset-0 animate-pulse bg-amber-400/5" />}
      <div className="relative mx-auto max-w-7xl px-4 py-5 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-5">
          <div className="flex items-start gap-4">
            <div
              className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${
                isSoon ? "bg-amber-400/20 ring-2 ring-amber-400/50" : "bg-cyan-400/15 ring-2 ring-cyan-400/30"
              }`}
            >
              <CalendarDays className={`h-6 w-6 ${isSoon ? "text-amber-300" : "text-cyan-300"}`} />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <p className={`text-xs font-bold uppercase tracking-widest ${isSoon ? "text-amber-300" : "text-cyan-300"}`}>
                  {isSoon ? "Coming Up Soon" : "Next Competition"}
                </p>
                {next.monthLong && (
                  <span className="inline-flex items-center rounded-full border border-fuchsia-400/40 bg-fuchsia-400/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-fuchsia-300">
                    Month Long Competition
                  </span>
                )}
              </div>
              <p className="mt-0.5 text-lg font-bold text-white sm:text-xl">
                {next.type} · <span className="font-semibold text-slate-300">{next.date}</span>
              </p>
              {(next.venue || next.time) && (
                <p className="mt-1 max-w-2xl text-xs text-slate-300 sm:text-sm">
                  {next.venue}
                  {next.venue && next.time ? " · " : ""}
                  {next.time}
                </p>
              )}
              {(next.rules || next.venueType) && (
                <div className="mt-2.5 flex flex-wrap gap-4">
                  {next.rules && (
                    <a
                      href={next.rules}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-300 hover:text-cyan-100 transition"
                    >
                      <FileText className="h-3.5 w-3.5 shrink-0" />
                      Competition Rules
                    </a>
                  )}
                  {next.venueType && (
                    <a
                      href={riskAssessments[next.venueType].file}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-400 hover:text-amber-200 transition"
                    >
                      <ShieldAlert className="h-3.5 w-3.5 shrink-0" />
                      {riskAssessments[next.venueType].label}
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4">
            {isToday ? (
              <span className="animate-pulse rounded-full bg-amber-400 px-5 py-2 text-sm font-bold text-slate-950 shadow-lg shadow-amber-400/50">
                Happening Today!
              </span>
            ) : (
              <div
                className={`flex items-center gap-3 rounded-2xl px-4 py-3 ring-1 sm:gap-4 ${
                  isSoon ? "bg-slate-950/50 ring-amber-400/40" : "bg-slate-950/40 ring-cyan-400/25"
                }`}
              >
                <Unit value={countdown.days}    label="days" />
                <span className={`font-bold text-xl mb-3 ${isSoon ? "text-amber-400/50" : "text-cyan-400/40"}`}>:</span>
                <Unit value={countdown.hours}   label="hrs" />
                <span className={`font-bold text-xl mb-3 ${isSoon ? "text-amber-400/50" : "text-cyan-400/40"}`}>:</span>
                <Unit value={countdown.minutes} label="mins" />
                <span className={`font-bold text-xl mb-3 ${isSoon ? "text-amber-400/50" : "text-cyan-400/40"}`}>:</span>
                <Unit value={countdown.seconds} label="secs" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FixturesPage() {
  const navigate = useNavigate();

  return (
    <div className="page-shell min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      <PageMeta
        title="Fixtures | Redruth Sea Angling Association"
        description="Upcoming shore and boat sea angling competition fixtures for Redruth Sea Angling Association, Cornwall's sea angling club since 1962."
        keywords="RSAA fixtures, sea angling competitions Cornwall, Redruth Sea Angling Association calendar"
        url="https://www.redruthseaanglingassociation.co.uk/fixtures"
      />
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-center gap-4">
          <button type="button" onClick={() => navigate(-1)}
            className="standalone-only group inline-flex items-center gap-1.5 rounded-full border border-cyan-400/20 bg-white/5 px-4 py-2 text-sm font-medium text-slate-200 shadow-lg shadow-black/20 backdrop-blur transition hover:-translate-x-0.5 hover:border-cyan-300/40 hover:bg-cyan-400/10 hover:text-white">
            ← Back
          </button>
          <div>
            <h1 className="heading-glow text-2xl font-bold text-white sm:text-3xl">Fixtures</h1>
            <p className="mt-1 text-sm text-slate-400">RSAA competition calendar 2026</p>
          </div>
        </div>

        <p className="text-sm font-semibold uppercase tracking-[0.3em] text-cyan-300">Fixtures</p>
        <h2 className="mt-4 text-3xl font-bold tracking-tight text-white sm:text-4xl">Upcoming fixtures calendar</h2>

        <div className="mt-6">
          <Link to="/scales-policy"
            className="inline-flex items-center gap-3 rounded-xl border border-cyan-300/40 bg-cyan-500/20 px-5 py-3 text-sm font-semibold text-cyan-100 shadow-lg shadow-cyan-950/30 transition hover:bg-cyan-500/30 hover:border-cyan-300/60">
            <Scale className="h-5 w-5 text-cyan-300" />
            <span>Please read: Weighing Scales Policy Notice <span className="ml-1 text-cyan-300">→</span></span>
          </Link>
        </div>

        <RiskAssessmentTabs />

        <div className="mt-6">
          <NextCompetitionBanner />
        </div>

        <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-4 sm:mt-10">
          {fixtureCards.map((fixture) => (
            <div key={`${fixture.date}-${fixture.type}`}
              className="flex flex-col rounded-[2rem] border border-white/10 bg-white/5 p-6 shadow-xl shadow-cyan-950/20 backdrop-blur">
              <div className="flex flex-wrap items-center gap-2">
                <p className="inline-block self-start rounded-md bg-sky-400/20 px-2.5 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-sky-300">{fixture.date}</p>
                {fixture.monthLong && (
                  <p className="inline-block self-start rounded-md border border-fuchsia-400/40 bg-fuchsia-400/10 px-2.5 py-1 text-xs font-bold uppercase tracking-[0.2em] text-fuchsia-300">Month Long Competition</p>
                )}
              </div>
              <h3 className="mt-3 text-lg font-semibold text-white sm:text-2xl">{fixture.type}</h3>
              <p className="mt-3 text-sm font-medium text-slate-200">{fixture.venue}</p>
              <p className="mt-4 text-sm leading-6 text-slate-300">{fixture.time}</p>
              {(fixture.rules || fixture.venueType) && (
                <div className="mt-5 flex flex-wrap gap-4">
                  {fixture.rules && (
                    <a href={fixture.rules} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-300 hover:text-cyan-100 transition">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5 shrink-0">
                        <path fillRule="evenodd" d="M4.5 2A1.5 1.5 0 003 3.5v13A1.5 1.5 0 004.5 18h11a1.5 1.5 0 001.5-1.5V7.621a1.5 1.5 0 00-.44-1.06l-4.12-4.122A1.5 1.5 0 0011.378 2H4.5zm4.75 6.75a.75.75 0 011.5 0v2.546l.943-1.048a.75.75 0 111.114 1.004l-2.25 2.5a.75.75 0 01-1.114 0l-2.25-2.5a.75.75 0 111.114-1.004l.943 1.048V8.75z" clipRule="evenodd" />
                      </svg>
                      Competition Rules
                    </a>
                  )}
                  {fixture.venueType && (
                    <a href={riskAssessments[fixture.venueType].file} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-400 hover:text-amber-200 transition">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5 shrink-0">
                        <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                      </svg>
                      {riskAssessments[fixture.venueType].label}
                    </a>
                  )}
                </div>
              )}
              <div className="mt-auto pt-5">
                <button
                  type="button"
                  onClick={() => {
                    const text = [fixture.type, fixture.date, fixture.venue, fixture.time].filter(Boolean).join("\n");
                    if (navigator.share) {
                      navigator.share({ title: `RSAA – ${fixture.type}`, text, url: window.location.origin });
                    } else {
                      navigator.clipboard?.writeText(`${fixture.type}\n${fixture.date}\n${text}\n${window.location.origin}`);
                    }
                  }}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-400 transition hover:text-slate-200"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 shrink-0">
                    <path d="M13 4.5a2.5 2.5 0 1 1 .702 1.737L6.97 9.604a2.518 2.518 0 0 1 0 .792l6.733 3.367a2.5 2.5 0 1 1-.671 1.341l-6.733-3.367a2.5 2.5 0 1 1 0-3.474l6.733-3.366A2.52 2.52 0 0 1 13 4.5z" />
                  </svg>
                  Share
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
